import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Waves, Wine, UtensilsCrossed, LifeBuoy, MapPin, Anchor, Clock, Users, Star, MessageCircle, Mail, Menu, X, Phone, Facebook, Instagram, BookOpen, ExternalLink, Send, ChevronLeft, ChevronRight, Award } from "lucide-react";
import { experiences, inclusions, upgrades, faqs, images, t, WHATSAPP_LINK, CONTACT_EMAIL, WHATSAPP, WHATSAPP_CHAT, testimonials, reviewStats, type Lang } from "@/content/site";
import { Link, useRouterState } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { sendContactEmail } from "@/lib/contact.functions";
import { ROUTE_META, getRelatedRoutes, getAltLangPath } from "@/content/seo";
import type { PageContent } from "@/content/landing-content";
import turismo360Badge from "@/assets/empresas-turismo-360.png?w=160&format=webp";
import turismoPortugalLogo from "@/assets/turismo-de-portugal.png?w=160&format=webp";
import tripadvisorLogo from "@/assets/tripadvisor.png?w=120&format=webp";
import crewClaudio from "@/assets/crew/crew-claudio.jpg";
import crewJorge from "@/assets/crew/crew-jorge.jpg";
import crewHugo from "@/assets/crew/crew-hugo.jpg";
import crewVira from "@/assets/crew/crew-vira.jpg";
import { ResponsiveImage } from "@/components/ResponsiveImage";
const CookieConsent = lazy(() => import("@/components/CookieConsent"));
import { track } from "@/lib/tracking";
import { useWhatsappGreeting } from "@/lib/whatsapp-greeting";
import heroMp4 from "@/assets/hero.mp4.asset.json";
import heroPoster from "@/assets/hero-poster.jpg.asset.json";
import heroPosterAvif from "@/assets/hero-poster.avif.asset.json";
import heroPosterWebp from "@/assets/hero-poster.webp.asset.json";
import { trustBadges, authorityLogo } from "@/data/trust";
import turismo360Trust from "@/assets/trust/turismo-360.png.asset.json";
import rgpdTrust from "@/assets/trust/rgpd.png.asset.json";
import amnTrust from "@/assets/trust/amn.png.asset.json";
import livroTrust from "@/assets/trust/livro-reclamacoes.webp.asset.json";
import insuranceTrust from "@/assets/trust/insurance.png.asset.json";
import dgrmTrust from "@/assets/trust/dgrm.png.asset.json";
import stripeTrust from "@/assets/trust/stripe.png.asset.json";

function openWhatsApp(message: string, source: string = "button") {
  const encoded = encodeURIComponent(message);
  const isMobile = /Android|iPhone|iPad|iPod|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const url = isMobile ? `whatsapp://send?phone=${WHATSAPP_CHAT}&text=${encoded}` : WHATSAPP_LINK(message);
  track("whatsapp_click", { source });
  window.open(url, "_blank", "noopener,noreferrer");
}

function YouTubeHero() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [enableVideo, setEnableVideo] = useState(false);
  const [playing, setPlaying] = useState(false);
  useEffect(() => {
    const nav: any = navigator;
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (conn?.saveData || reduced) return;
    // Only load the video after the FIRST user interaction. This guarantees
    // the preloaded AVIF poster wins the LCP measurement (Lighthouse never
    // simulates interaction). Real users see the video kick in instantly
    // on their first scroll/touch/click.
    const events = ["pointerdown", "touchstart", "keydown", "scroll", "wheel"] as const;
    const opts = { once: true, passive: true, capture: true } as const;
    const load = () => {
      setEnableVideo(true);
      events.forEach((e) => window.removeEventListener(e, load, opts as any));
    };
    events.forEach((e) => window.addEventListener(e, load, opts as any));
    return () => events.forEach((e) => window.removeEventListener(e, load, opts as any));
  }, []);
  return (
    <div className="absolute inset-0 overflow-hidden">
      <picture>
        <source type="image/avif" srcSet={heroPosterAvif.url} />
        <source type="image/webp" srcSet={heroPosterWebp.url} />
        <img
          src={heroPoster.url}
          alt=""
          aria-hidden="true"
          width={960}
          height={540}
          // @ts-expect-error — valid HTML attribute
          fetchpriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </picture>
      {enableVideo && (
        <video
          ref={videoRef}
          className={`absolute left-1/2 top-1/2 h-full w-full min-h-full min-w-full -translate-x-1/2 -translate-y-1/2 object-cover pointer-events-none transition-opacity duration-700 ${playing ? "opacity-100" : "opacity-0"}`}
          src={heroMp4.url}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          onPlaying={() => setPlaying(true)}
        />
      )}
      <div className="absolute inset-0 bg-gradient-hero" />
    </div>
  );
}

export function Header({ lang }: { lang: Lang }) {
  const dict = t[lang];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const currentPath = useRouterState({ select: (s) => s.location.pathname });
  const alt = getAltLangPath(currentPath);
  const home = lang === "pt" ? "/pt" : "/";
  const isHome = currentPath === "/" || currentPath === "/pt" || currentPath === "/pt/";
  const hash = (h: string) => (isHome ? h : `${home}${h}`);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const links = [
    { href: hash("#experiences"), label: dict.nav.experiences },
    { href: hash("#inclusions"), label: dict.nav.inclusions },
    { href: hash("#upgrades"), label: dict.nav.upgrades },
    { href: hash("#yacht"), label: dict.nav.yacht },
    { href: hash("#about"), label: dict.nav.about },
    { href: hash("#crew"), label: dict.nav.crew },
    { href: hash("#faq"), label: dict.nav.faq },
    { href: hash("#contact"), label: dict.nav.contact },
    { href: lang === "pt" ? "/pt/centro-de-confianca" : "/trust-centre", label: lang === "pt" ? "Centro de Confiança" : "Trust Centre" },
    { href: lang === "pt" ? "/pt/destinos" : "/destinations", label: lang === "pt" ? "Destinos" : "Destinations" },
  ];
  return (
    <header className={`fixed top-0 z-50 w-full bg-emerald-deep transition-shadow duration-300 ${scrolled ? "shadow-soft" : ""}`}>
      <div className={`mx-auto flex max-w-[1500px] items-center justify-between gap-3 px-4 sm:px-6 lg:grid lg:grid-cols-[1fr_auto_1fr] transition-all duration-300 ${scrolled ? "py-4" : "py-5 lg:py-8"}`}>
        <a href={home} className="flex flex-col leading-none text-[oklch(0.98_0.01_95)] min-w-0">
          <span className="font-display text-lg sm:text-2xl tracking-[0.14em] sm:tracking-[0.18em] uppercase font-semibold whitespace-nowrap">
            Morning<span className="text-gold">star</span>
          </span>
          <span className="mt-1 text-[8px] sm:text-[9px] tracking-[0.26em] sm:tracking-[0.3em] uppercase text-gold/90 font-medium whitespace-nowrap">
            Vilamoura Dreams
          </span>
        </a>
        <nav className="hidden lg:flex items-center gap-9 text-[11px] tracking-[0.22em] uppercase text-[oklch(0.98_0.01_95)] font-medium">
          {links.slice(0, 5).map((l) => (
            <a key={l.href} href={l.href} className="hover:text-gold transition-colors">{l.label}</a>
          ))}
          <a href={lang === "pt" ? "/pt/destinos" : "/destinations"} className="hover:text-gold transition-colors">{lang === "pt" ? "Destinos" : "Destinations"}</a>
          <a href={lang === "pt" ? "/pt/centro-de-confianca" : "/trust-centre"} className="hover:text-gold transition-colors">{lang === "pt" ? "Confiança" : "Trust"}</a>
          <a href={hash("#contact")} className="hover:text-gold transition-colors">{dict.nav.contact}</a>
        </nav>
        <div className="flex items-center justify-end gap-2 sm:gap-3">
          <a href={alt.en} className={`text-[11px] tracking-[0.22em] uppercase font-medium transition-colors ${lang === "en" ? "text-gold" : "text-[oklch(0.95_0.01_95)] hover:text-gold"}`}>EN</a>
          <span className="text-[oklch(0.95_0.01_95)]/40">|</span>
          <a href={alt.pt} className={`text-[11px] tracking-[0.22em] uppercase font-medium transition-colors ${lang === "pt" ? "text-gold" : "text-[oklch(0.95_0.01_95)] hover:text-gold"}`}>PT</a>
          <button onClick={() => setOpen(!open)} className="lg:hidden text-[oklch(0.95_0.01_95)] ml-1 sm:ml-2" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden bg-emerald-deep border-t border-[oklch(1_0_0/0.08)] px-6 py-4 flex flex-col gap-3">
          {links.map((l) => (
            <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-[oklch(0.95_0.01_95)] py-2 border-b border-[oklch(1_0_0/0.06)] text-xs tracking-[0.22em] uppercase">{l.label}</a>
          ))}
        </nav>
      )}
    </header>
  );
}

export type HeroOverride = {
  eyebrow: string;
  title: string;
  titleAccent: string;
  sub: string;
  ctaPrimary?: string;
  ctaSecondary?: string;
};

function Hero({ lang, override }: { lang: Lang; override?: HeroOverride }) {
  const dict = t[lang];
  const hero = override ?? dict.hero;
  const heroWaMsg = useWhatsappGreeting(lang);
  return (
    <section id="top" className="relative min-h-[100vh] flex items-center overflow-hidden">
      <YouTubeHero />
      <div className="relative z-10 mx-auto w-full max-w-[1500px] px-6 pt-32 pb-24">
        <p className="text-[11px] tracking-[0.32em] uppercase text-[oklch(0.95_0.01_95)] font-medium mb-8">
          {hero.eyebrow}
        </p>
        <h1 className="font-display text-[2.8rem] leading-[1.02] sm:text-6xl lg:text-[5.5rem] text-[oklch(0.98_0.01_95)] max-w-[16ch] font-normal">
          {hero.title}
          <span className="block italic text-gold font-normal">{hero.titleAccent.trim()}</span>
        </h1>
        <p className="mt-10 max-w-xl text-base sm:text-lg text-[oklch(0.94_0.02_95)] leading-relaxed">{hero.sub}</p>
        <div className="mt-10 flex flex-wrap gap-4">
          <a href="#experiences" className="inline-flex items-center bg-gold px-9 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] text-emerald-deep hover:bg-[oklch(0.85_0.14_85)] transition-colors">
            {hero.ctaPrimary ?? dict.hero.ctaPrimary}
          </a>
          <a
            href={WHATSAPP_LINK(heroWaMsg)}
            onClick={() => track("whatsapp_click", { source: "hero" })}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-[oklch(0.95_0.01_95/0.6)] px-9 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] text-[oklch(0.98_0.01_95)] hover:border-gold hover:text-gold transition-colors"
          >
            <MessageCircle size={14} />
            {hero.ctaSecondary ?? dict.hero.ctaSecondary}
          </a>
        </div>
      </div>
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-[oklch(0.92_0.02_95)] animate-bounce">
        <ChevronDown size={22} />
      </div>
    </section>
  );
}

// ---------- Brand logos (inline SVG) ----------
function GoogleLogo({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.6 2.4-7.2 2.4-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.2C40.9 35.8 44 30.4 44 24c0-1.3-.1-2.3-.4-3.5z"/>
    </svg>
  );
}
function TripadvisorLogo({ size = 44 }: { size?: number }) {
  return (
    <img src={tripadvisorLogo} alt="Tripadvisor" width={size} height={size} className="object-contain" loading="lazy" />
  );
}
function RnaatLogo() {
  return (
    <div className="flex flex-col items-center leading-none gap-2">
      <img
        src={turismoPortugalLogo}
        alt="Turismo de Portugal"
        width={84}
        height={32}
        className="h-8 w-auto object-contain"
        loading="lazy"
      />
      <span className="font-display text-base tracking-[0.18em] text-emerald-deep mt-1">RNAAT 743/2022</span>
    </div>
  );
}
function TurismoPortugalLogo() {
  return (
    <img
      src={turismo360Badge}
      alt="Empresa Turismo 360 — Turismo de Portugal"
      width={69}
      height={48}
      className="h-12 w-auto object-contain"
      loading="lazy"
    />
  );
}

export function TrustBar({ lang }: { lang: Lang }) {
  const exclusivity = lang === "en"
    ? "Private charters only. Limited availability."
    : "Apenas charters privados. Disponibilidade limitada.";
  const items = [
    { logo: <GoogleLogo />, label: lang === "en" ? "Google" : "Google", rating: reviewStats.google.rating, count: reviewStats.google.count, href: reviewStats.google.url },
    { logo: <TripadvisorLogo />, label: "Tripadvisor", rating: reviewStats.tripadvisor.rating, count: reviewStats.tripadvisor.count, href: reviewStats.tripadvisor.url },
    { logo: <RnaatLogo />, label: lang === "en" ? "Licensed tour operator" : "Operador licenciado", href: null as string | null },
    { logo: <TurismoPortugalLogo />, label: "Turismo 360", href: null as string | null },
  ];
  return (
    <section className="bg-white text-emerald-deep border-y border-emerald-deep/10">
      <div className="mx-auto max-w-7xl px-5 pt-6 pb-2 text-center">
        <p className="text-[11px] sm:text-[12px] uppercase tracking-[0.28em] text-gold-deep font-medium">
          {exclusivity}
        </p>
      </div>
      <div className="mx-auto max-w-7xl px-5 pt-6 pb-10 grid grid-cols-2 md:grid-cols-4 gap-8 items-center text-center">
        {items.map((it, i) => {
          const inner = (
            <div className="flex flex-col items-center gap-2">
              <div className="min-h-10 flex items-center justify-center">{it.logo}</div>
              {it.rating !== undefined ? (
                <>
                  <div className="flex items-center gap-1">
                    {[0,1,2,3,4].map((s) => <Star key={s} size={12} className="text-gold" fill="currentColor" />)}
                    <span className="ml-1 text-[12px] font-semibold">{it.rating.toFixed(1).replace(".", lang === "en" ? "." : ",")}</span>
                  </div>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-deep/70">
                    {it.count} {lang === "en" ? "reviews on" : "avaliações no"} {it.label}
                  </p>
                </>
              ) : (
                <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-deep/70">{it.label}</p>
              )}
            </div>
          );
          return it.href ? (
            <a key={i} href={it.href} target="_top" rel="noopener noreferrer" className="hover:opacity-90 transition-opacity">{inner}</a>
          ) : (
            <div key={i}>{inner}</div>
          );
        })}
      </div>
    </section>
  );
}

export function Testimonials({ lang }: { lang: Lang }) {
  const dict = t[lang];
  const s = dict.testimonialsSection;
  const [idx, setIdx] = useState(0);
  const total = testimonials.length;
  const next = () => setIdx((i) => (i + 1) % total);
  const prev = () => setIdx((i) => (i - 1 + total) % total);
  useEffect(() => {
    const id = setInterval(next, 7000);
    return () => clearInterval(id);
  }, []);
  const tst = testimonials[idx];
  const logo = tst.source === "google" ? <GoogleLogo size={20} /> : <TripadvisorLogo size={22} />;
  const sourceLabel = tst.source === "google" ? "Google" : "Tripadvisor";
  const sourceUrl = tst.source === "google" ? reviewStats.google.url : reviewStats.tripadvisor.url;
  const initials = tst.author
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? "")
    .join("") || "·";
  return (
    <section id="testimonials" className="py-24 sm:py-32 bg-[oklch(0.97_0.01_95)]">
      <div className="mx-auto max-w-5xl px-5">
        <div className="text-center max-w-2xl mx-auto">
          <p className="eyebrow mb-4">{s.eyebrow}</p>
          <h2 className="font-display text-4xl sm:text-5xl text-emerald-deep leading-[1.05]">
            {s.heading}<em className="not-italic text-gold-deep">{s.headingAccent}</em>
          </h2>
          <p className="mt-5 text-foreground/75">{s.lead}</p>
        </div>
        <div className="mt-14 relative">
          <article className="bg-white rounded-2xl shadow-soft border border-black/5 px-6 sm:px-12 py-10 sm:py-14 min-h-[320px] flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-3">
                {logo}
                <a href={sourceUrl} target="_top" rel="noopener noreferrer" className="text-xs uppercase tracking-[0.22em] font-semibold text-emerald-deep hover:text-gold-deep transition-colors">
                  {sourceLabel}
                </a>
                <span className="ml-auto flex items-center gap-0.5">
                  {[0,1,2,3,4].map((i) => <Star key={i} size={14} className="text-gold-deep" fill="currentColor" />)}
                </span>
              </div>
              <blockquote className="mt-6 font-display text-xl sm:text-2xl text-emerald-deep leading-snug">
                “{tst.body[lang]}”
              </blockquote>
            </div>
            <footer className="mt-8 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
              <span aria-hidden className="h-9 w-9 rounded-full bg-gold/20 text-emerald-deep flex items-center justify-center font-display text-sm font-semibold mr-1 self-center">
                {initials}
              </span>
              <span className="font-semibold text-emerald-deep">{tst.author}</span>
              {tst.location && <span className="text-foreground/60">· {tst.location}</span>}
              <span className="text-foreground/60">· {tst.date}</span>
              <span className="text-foreground/60">· {lang === "en" ? "via" : "via"} {sourceLabel}</span>
              {tst.tour && <span className="text-foreground/60 italic">— {tst.tour}</span>}
            </footer>
          </article>
          <div className="mt-6 flex items-center justify-center gap-4">
            <button type="button" aria-label="Previous" onClick={prev} className="h-10 w-10 rounded-full border border-emerald-deep/20 text-emerald-deep hover:bg-emerald-deep hover:text-white transition-colors flex items-center justify-center">
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  aria-label={`Go to review ${i + 1}`}
                  onClick={() => setIdx(i)}
                  className="group flex h-6 w-6 items-center justify-center"
                >
                  <span
                    aria-hidden="true"
                    className={`block h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-gold-deep" : "w-1.5 bg-emerald-deep/25 group-hover:bg-emerald-deep/50"}`}
                  />
                </button>
              ))}
            </div>
            <button type="button" aria-label="Next" onClick={next} className="h-10 w-10 rounded-full border border-emerald-deep/20 text-emerald-deep hover:bg-emerald-deep hover:text-white transition-colors flex items-center justify-center">
              <ChevronRight size={18} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function SectionHeading({ eyebrow, heading, accent, lead }: { eyebrow: string; heading: string; accent: string; lead?: string }) {
  return (
    <div className="max-w-3xl">
      <p className="eyebrow mb-4">{eyebrow}</p>
      <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-emerald-deep leading-[1.05]">
        {heading} <em className="not-italic text-gold-deep">{accent}</em>
      </h2>
      {lead && <p className="mt-5 text-lg text-muted-foreground max-w-2xl">{lead}</p>}
    </div>
  );
}

function Experiences({ lang }: { lang: Lang }) {
  const dict = t[lang];
  return (
    <section id="experiences" className="py-24 sm:py-32 bg-sand">
      <div className="mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={dict.expSection.eyebrow} heading={dict.expSection.heading} accent={dict.expSection.headingAccent} lead={dict.expSection.lead} />
        <div className="mt-16 grid gap-7 md:grid-cols-2 lg:grid-cols-3">
          {experiences.map((e) => (
            <ExperienceCard key={e.id} e={e} lang={lang} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ExperienceCard({ e, lang }: { e: (typeof experiences)[number]; lang: Lang }) {
  const [expanded, setExpanded] = useState(false);
  const cardWaMsg = useWhatsappGreeting(lang);
  const fullText = e.description[lang];
  const isLong = fullText.length > 220;
  const firstPara = fullText.split("\n\n")[0];
  const shown = expanded || !isLong ? fullText : firstPara;
  const more = lang === "en" ? "Show more" : "Mostrar mais";
  const less = lang === "en" ? "Show less" : "Mostrar menos";
  return (
    <article className="group relative overflow-hidden bg-card shadow-soft hover:shadow-gold transition-shadow flex flex-col">
      <div className="aspect-[4/3] overflow-hidden">
        <ResponsiveImage picture={e.image} alt={e.title[lang]} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" width={1024} height={768} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
      {e.badge && (
        <span className="absolute top-4 left-4 bg-gold text-[oklch(0.18_0.03_165)] text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-none">
          {e.badge[lang]}
        </span>
      )}
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-4 text-xs uppercase tracking-widest text-emerald font-medium">
          <span className="inline-flex items-center gap-1.5"><Clock size={13} />{e.duration[lang]}</span>
          <span className="inline-flex items-center gap-1.5"><Users size={13} />18</span>
        </div>
        <p className="mt-3 text-gold-deep text-sm font-medium">{e.times}</p>
        <h3 className="mt-2 font-display text-2xl text-emerald-deep">{e.title[lang]}</h3>
        <p className="mt-1 text-sm italic text-muted-foreground">{e.tagline[lang]}</p>
        <div className="mt-4 text-sm leading-relaxed text-foreground/80 whitespace-pre-line">
          {shown}
          {isLong && !expanded && <span className="text-foreground/50">…</span>}
        </div>
        {isLong && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 self-start text-[11px] font-semibold uppercase tracking-[0.22em] text-gold-deep hover:text-emerald-deep transition-colors"
          >
            {expanded ? less : more}
          </button>
        )}
        <ul className="mt-4 space-y-1.5 text-sm text-foreground/80">
          {e.highlights[lang].map((h, i) => (
            <li key={i} className="flex gap-2"><span className="text-gold mt-1">◆</span>{h}</li>
          ))}
        </ul>
        {e.map && (
          <div className="mt-5 overflow-hidden border border-[oklch(0.78_0.14_85/0.3)] bg-[oklch(0.97_0.01_95)]">
            <div className="px-4 py-2.5 border-b border-[oklch(0.78_0.14_85/0.25)] flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-gold-deep font-semibold">
              <MapPin size={12} className="text-gold" /> {lang === "en" ? "Route map" : "Mapa da rota"}
            </div>
            <img src={e.map} alt={`${e.title[lang]} route map`} loading="lazy" decoding="async" className="w-full h-auto block" />
            {e.route && (
              <p className="px-4 py-3 text-xs leading-relaxed text-foreground/75">{e.route[lang]}</p>
            )}
          </div>
        )}
        <a
          href={WHATSAPP_LINK(cardWaMsg)}
          onClick={() => track("whatsapp_click", { source: "experience_card" })}
          target="_blank" rel="noopener noreferrer"
          className="mt-auto pt-6 inline-flex items-center gap-2 self-start text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-deep"
        >
          <span className="inline-flex items-center gap-2 rounded-none bg-gold px-5 py-2.5 shadow-gold hover:bg-[oklch(0.85_0.14_85)] hover:gap-3 transition-all">
            <MessageCircle size={13} /> {lang === "en" ? "Talk to Concierge" : "Falar com o Concierge"}
          </span>
        </a>
      </div>
    </article>
  );
}

function Inclusions({ lang }: { lang: Lang }) {
  const dict = t[lang];
  const inc = inclusions[lang];
  const items = [
    { icon: Wine, title: dict.inclusionsSection.drinks, body: inc.drinks },
    { icon: UtensilsCrossed, title: dict.inclusionsSection.food, body: inc.food },
    { icon: Waves, title: dict.inclusionsSection.activities, body: inc.activities },
    { icon: LifeBuoy, title: dict.inclusionsSection.service, body: inc.service },
  ];
  return (
    <section id="inclusions" className="py-24 sm:py-32 bg-emerald-deep text-[oklch(0.95_0.01_95)] relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: `url(${images.sushi})`, backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="relative mx-auto max-w-7xl px-5">
        <div className="max-w-3xl">
          <p className="eyebrow text-gold mb-4">{dict.inclusionsSection.eyebrow}</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
            {dict.inclusionsSection.heading} <em className="not-italic text-gold">{dict.inclusionsSection.headingAccent}</em>
          </h2>
        </div>
        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-4">
          {items.map(({ icon: Icon, title, body }) => (
            <div key={title} className="border-t border-[oklch(0.78_0.14_85/0.3)] pt-6">
              <Icon className="text-gold" size={28} strokeWidth={1.4} />
              <h3 className="mt-5 font-display text-2xl text-[oklch(0.98_0.01_95)]">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[oklch(0.88_0.02_95)]">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function UpgradeCard({ u, lang }: { u: (typeof upgrades)[number]; lang: Lang }) {
  const msg = useWhatsappGreeting(lang);
  return (
    <article className="group flex flex-col overflow-hidden bg-card shadow-soft hover:shadow-gold transition-shadow">
      <div className="relative aspect-[5/4] overflow-hidden">
        <ResponsiveImage picture={u.image} alt={u.title[lang]} sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw" width={1024} height={820} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <span className="absolute top-3 left-3 bg-emerald-deep/90 text-[oklch(0.98_0.01_95)] text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-none">
          {u.category[lang]}
        </span>
        <span className="absolute top-3 right-3 bg-gold text-emerald-deep text-[10px] uppercase tracking-widest font-semibold px-3 py-1.5 rounded-none">
          {u.unit[lang]}
        </span>
      </div>
      <div className="flex flex-col flex-1 p-5">
        <h3 className="font-display text-xl text-emerald-deep leading-tight">{u.title[lang]}</h3>
        <p className="mt-1 text-xs text-gold-deep font-medium">{u.duration[lang]}</p>
        <p className="mt-3 text-sm text-foreground/80 flex-1">{u.description[lang]}</p>
        <div className="mt-5 pt-4 border-t border-[oklch(0.78_0.14_85/0.25)] flex items-center justify-end gap-3">
          <a
            href={WHATSAPP_LINK(msg)}
            onClick={() => track("whatsapp_click", { source: "upgrade" })}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-gold px-4 py-2.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-emerald-deep hover:bg-[oklch(0.85_0.14_85)] transition-colors rounded-none"
          >
            <MessageCircle size={12} /> {lang === "en" ? "Concierge" : "Concierge"}
          </a>
        </div>
      </div>
    </article>
  );
}

function Upgrades({ lang }: { lang: Lang }) {
  const dict = t[lang];
  return (
    <section id="upgrades" className="py-24 sm:py-32 bg-background">
      <div className="mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={dict.upgradesSection.eyebrow} heading={dict.upgradesSection.heading} accent={dict.upgradesSection.headingAccent} lead={dict.upgradesSection.lead} />
        <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {upgrades.map((u) => <UpgradeCard key={u.id} u={u} lang={lang} />)}
        </div>
      </div>
    </section>
  );
}

function Yacht({ lang }: { lang: Lang }) {
  const dict = t[lang];
  return (
    <section id="yacht" className="py-24 sm:py-32 bg-background">
      <div className="mx-auto max-w-7xl px-5 grid lg:grid-cols-2 gap-14 items-center">
        <ResponsiveImage picture={images.groupBow} alt={lang === "en" ? "Guests celebrating on Morningstar's bow" : "Convidados a celebrar na proa do Morningstar"} sizes="(min-width: 1024px) 50vw, 100vw" width={1200} height={900} className="shadow-soft aspect-[4/3] object-cover w-full" />
        <div>
          <p className="eyebrow mb-4">{dict.yachtSection.eyebrow}</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-emerald-deep leading-[1.05]">
            {dict.yachtSection.heading} <em className="not-italic text-gold-deep">{dict.yachtSection.headingAccent}</em>
          </h2>
          <p className="mt-6 text-lg text-foreground/80 leading-relaxed">{dict.yachtSection.body}</p>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-gold pt-8">
            <div><p className="font-display text-4xl text-gold-deep">18</p><p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{dict.yachtSection.g}</p></div>
            <div><p className="font-display text-4xl text-gold-deep">200</p><p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{dict.yachtSection.y}</p></div>
            <div><p className="font-display text-4xl text-gold-deep">40+</p><p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{dict.yachtSection.yrs}</p></div>
            <div><p className="font-display text-base text-gold-deep">RNAAT</p><p className="mt-1 text-xs uppercase tracking-widest text-muted-foreground">{dict.yachtSection.lic}</p></div>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutUs({ lang }: { lang: Lang }) {
  const dict = t[lang];
  const a = dict.aboutSection;
  return (
    <section id="about" className="py-24 sm:py-32 bg-sand">
      <div className="mx-auto max-w-7xl px-5 grid lg:grid-cols-2 gap-14 items-center">
        <ResponsiveImage
          picture={images.aboutLunch}
          alt={lang === "en" ? "Morningstar yacht in Marina de Vilamoura, Algarve" : "Iate Morningstar na Marina de Vilamoura, Algarve"}
          sizes="(min-width: 1024px) 50vw, 100vw"
          width={1600}
          height={1067}
          className="shadow-soft aspect-[3/2] object-cover w-full"
        />
        <div>
          <p className="eyebrow mb-4">{a.eyebrow}</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-emerald-deep leading-[1.05]">
            {a.heading}<em className="not-italic text-gold-deep">{a.headingAccent}</em>
          </h2>
          <p className="mt-6 text-lg text-foreground/80 leading-relaxed">{a.body1}</p>
          <p className="mt-4 text-lg text-foreground/80 leading-relaxed">{a.body2}</p>
        </div>
      </div>
    </section>
  );
}

function Gallery({ lang }: { lang: Lang }) {
  const dict = t[lang];
  const gallery = [
    images.benagilInterior, images.sushi, images.bowChampagne,
    images.swimJump, images.yachtCliffs, images.deckCheers,
    images.toastCouples, images.towTube, images.birthday,
    images.tenderCaves, images.sunset, images.lostCaves,
  ];
  return (
    <section className="py-24 bg-emerald-deep">
      <div className="mx-auto max-w-7xl px-5">
        <h2 className="font-display text-3xl sm:text-4xl text-[oklch(0.98_0.01_95)] text-center mb-12">{dict.galleryHeading}</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-0">
          {gallery.map((src, i) => (
            <ResponsiveImage key={i} picture={src} alt="Morningstar yacht moments" sizes="(min-width: 768px) 33vw, 50vw" width={800} height={600} className="w-full h-full object-cover block aspect-[4/3]" />
          ))}
        </div>
      </div>
    </section>
  );
}

const CREW_PHOTOS = [crewClaudio, crewJorge, crewHugo, crewVira];

const TRUST_LOGOS: Record<string, string> = {
  t360: turismo360Trust.url,
  gdpr: rgpdTrust.url,
  survey: amnTrust.url,
  consumer: livroTrust.url,
  insurance: insuranceTrust.url,
  crew: dgrmTrust.url,
  payments: stripeTrust.url,
};

function LicencesStrip({ lang }: { lang: Lang }) {
  const isPt = lang === "pt";
  const eyebrow = isPt ? "Licenças & certificados" : "Licences & certificates";
  const heading = isPt ? "O que sustenta a operação" : "What backs the operation";
  const ctaLabel = isPt ? "Ver Centro de Confiança completo" : "See full Trust Centre";
  const ctaHref = isPt ? "/pt/centro-de-confianca" : "/trust-centre";
  return (
    <section id="licences" className="py-20 sm:py-24 bg-background border-t border-border/40">
      <div className="mx-auto max-w-7xl px-5">
        <div className="text-center">
          <p className="text-[11px] uppercase tracking-[0.22em] text-gold-deep font-semibold">{eyebrow}</p>
          <h2 className="mt-2 font-display text-3xl sm:text-4xl text-emerald-deep">{heading}</h2>
        </div>
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
          {trustBadges.map((b) => {
            const logo = TRUST_LOGOS[b.id] ?? (b.logoDomain ? authorityLogo(b.logoDomain) : null);
            return (
              <a
                key={b.id}
                href={b.authorityUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center text-center rounded-2xl border border-input bg-background p-5 transition hover:border-gold hover:shadow-soft"
              >
                <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-border/40 bg-white p-1.5">
                  {logo ? (
                    <img src={logo} alt={b.authority} loading="lazy" className="h-full w-full object-contain" />
                  ) : (
                    <Award className="h-6 w-6 text-gold-deep" />
                  )}
                </div>
                <h3 className="mt-3 font-serif text-sm sm:text-base leading-tight text-emerald-deep">
                  {b.title[lang]}
                </h3>
                <span className="mt-2 inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-gold-deep">
                  {b.authority}
                  <ExternalLink className="h-3 w-3" />
                </span>
              </a>
            );
          })}
        </div>
        <div className="mt-10 text-center">
          <Link to={ctaHref} className="inline-flex items-center gap-2 text-sm text-emerald-deep underline underline-offset-4 hover:text-gold-deep">
            {ctaLabel}
            <ChevronRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function ExcellenceCrew({ lang }: { lang: Lang }) {
  const dict = t[lang];
  const s = dict.crewSection;
  return (
    <section id="crew" className="py-24 sm:py-32 bg-[oklch(0.995_0.003_95)]">
      <div className="mx-auto max-w-7xl px-5">
        <SectionHeading eyebrow={s.eyebrow} heading={s.heading} accent={s.headingAccent} lead={s.lead} />
        <p className="mt-10 mx-auto max-w-3xl text-center text-lg sm:text-xl text-emerald-deep/85 leading-relaxed font-light">
          {s.prose.map((seg, i) =>
            seg.bold ? (
              <strong key={i} className="font-semibold text-emerald-deep">{seg.bold}</strong>
            ) : (
              <span key={i}>{seg.text}</span>
            )
          )}
        </p>
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {s.members.map((m, i) => (
            <figure key={m.name} className="group">
              <div className="overflow-hidden bg-emerald-deep/5 shadow-soft">
                <img
                  src={CREW_PHOTOS[i]}
                  alt={`${m.role} ${m.name}`}
                  loading="lazy"
                  decoding="async"
                  width={900}
                  height={1200}
                  className="w-full h-full aspect-[3/4] object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <figcaption className="mt-4">
                <p className="font-display text-xl sm:text-2xl text-emerald-deep leading-tight">{m.name}</p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-gold-deep">{m.role}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQ({ lang }: { lang: Lang }) {
  const dict = t[lang];
  const list = faqs[lang];
  const [open, setOpen] = useState<number | null>(0);
  const faqHref = lang === "pt" ? "/pt/faq" : "/faq";
  const moreLabel = lang === "pt" ? "Ver as 15 perguntas completas →" : "See the full 15-question FAQ →";
  return (
    <section id="faq" className="py-24 sm:py-32 bg-sand">
      <div className="mx-auto max-w-4xl px-5">
        <SectionHeading eyebrow={dict.faqSection.eyebrow} heading={dict.faqSection.heading} accent={dict.faqSection.headingAccent} />
        <div className="mt-12 divide-y divide-[oklch(0.78_0.14_85/0.3)] border-y border-[oklch(0.78_0.14_85/0.3)]">
          {list.map((f, i) => (
            <div key={i}>
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between gap-6 py-5 text-left">
                <span className="font-display text-lg sm:text-xl text-emerald-deep">{f.q}</span>
                <ChevronDown size={18} className={`text-gold-deep shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <p className="pb-5 text-foreground/80 leading-relaxed">{f.a}</p>}
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to={faqHref} className="inline-block text-[11px] uppercase tracking-[0.22em] text-gold-deep font-semibold hover:text-emerald-deep transition-colors">
            {moreLabel}
          </Link>
        </div>
      </div>
    </section>
  );
}

function Contact({ lang }: { lang: Lang }) {
  const dict = t[lang];
  const f = dict.contact.form;
  const contactWaMsg = useWhatsappGreeting(lang);
  const [primary, setPrimary] = useState<"wa" | "email">("wa");
  const [form, setForm] = useState({ name: "", phone: "", email: "", cruise: "", guests: "", date: "", comments: "" });
  const [err, setErr] = useState<string | null>(null);
  const [status, setStatus] = useState<"idle" | "sending" | "ok" | "error">("idle");
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const submitEmail = useServerFn(sendContactEmail);
  const update = (k: keyof typeof form) => (e: any) => setForm({ ...form, [k]: e.target.value });

  const cruiseOptions = useMemo(() => experiences.map((e) => e.title[lang]), [lang]);

  const message = useMemo(() => {
    const lines = [
      f.intro,
      "",
      `• ${f.name}: ${form.name || "—"}`,
      `• ${f.cruise}: ${form.cruise || "—"}`,
      form.date && `• ${f.date}: ${form.date}`,
      form.guests && `• ${f.guests}: ${form.guests}`,
      form.phone && `• ${f.phone}: ${form.phone}`,
      form.email && `• ${f.emailLbl}: ${form.email}`,
      form.comments && `• ${f.comments}: ${form.comments}`,
      "",
      f.thanks,
    ].filter(Boolean).join("\n");
    return lines;
  }, [form, f]);

  const validate = (intent: "wa" | "email") => {
    const name = form.name.trim();
    const email = form.email.trim();
    const phone = form.phone.trim();
    const comments = form.comments.trim();
    if (!name || !form.cruise) { setErr(f.validation); return false; }
    if (intent === "wa" && !phone) { setErr(f.validation); return false; }
    if (intent === "email" && !email) { setErr(f.validation); return false; }
    if (name.length > 100) { setErr(f.validation); return false; }
    if (phone && (phone.length > 30 || !/^[+()\-\s\d]{6,30}$/.test(phone))) { setErr(f.validation); return false; }
    if (email && (email.length > 255 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) { setErr(f.validation); return false; }
    if (comments.length > 1000) { setErr(f.validation); return false; }
    setErr(null);
    return true;
  };

  const sendWa = (e: React.FormEvent) => {
    e.preventDefault();
    setPrimary("wa");
    if (!validate("wa")) return;
    track("whatsapp_form_submit");
    sendWaFlow("form_wa");
  };
  const sendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setPrimary("email");
    if (!validate("email")) return;
    setStatus("sending");
    try {
      const res = await submitEmail({
        data: {
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          cruise: form.cruise,
          guests: form.guests,
          date: form.date,
          comments: form.comments.trim(),
          lang,
        },
      });
      if (res?.ok) {
        track("email_form_submit", { transport: "resend" });
        setStatus("ok");
        setErrorDetail(null);
        setForm({ name: "", phone: "", email: "", cruise: "", guests: "", date: "", comments: "" });
      } else {
        setErrorDetail((res as { detail?: string } | undefined)?.detail ?? null);
        setStatus("error");
      }
    } catch (e) {
      setErrorDetail(String((e as Error)?.message ?? e));
      setStatus("error");
    }
  };
  const sendWaFlow = (waSource: string = "form") => {
    const subj = `${f.subtitle} — ${form.name} — ${form.cruise}`;
    void subj;
    openWhatsApp(message, waSource);
  };

  return (
    <section id="contact" className="py-24 sm:py-32 bg-ink text-[oklch(0.95_0.01_95)]">
      <div className="mx-auto max-w-6xl px-5">
        <div className="text-center">
          <Anchor className="mx-auto text-gold" size={32} strokeWidth={1.4} aria-hidden="true" />
          <p className="eyebrow text-gold mt-6">{dict.contact.eyebrow}</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            {dict.contact.heading}<em className="not-italic text-gold">{dict.contact.headingAccent}</em>
          </h2>
          <p className="mt-6 max-w-2xl mx-auto text-[oklch(0.88_0.02_95)]">{dict.contact.lead}</p>
        </div>

        <form onSubmit={primary === "wa" ? sendWa : sendEmail} className="mt-12 grid lg:grid-cols-[1.05fr_1fr] gap-8 items-start">
          <div className="bg-[oklch(1_0_0/0.04)] border border-[oklch(1_0_0/0.1)] rounded-2xl p-7 sm:p-9">
            <div className="flex items-center justify-between mb-6">
              <p className="text-xs uppercase tracking-[0.22em] text-gold font-semibold">{f.title}</p>
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 bg-gold/15 text-gold rounded-full">{f.subtitle}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={`${f.name} *`}><input required maxLength={100} value={form.name} onChange={update("name")} className={inputCls} autoComplete="name" /></Field>
              <Field label={`${f.phone}${primary === "wa" ? " *" : ""}`}><input type="tel" maxLength={30} value={form.phone} onChange={update("phone")} className={inputCls} autoComplete="tel" inputMode="tel" /></Field>
              <Field label={`${f.emailLbl}${primary === "email" ? " *" : ""}`}><input type="email" maxLength={255} value={form.email} onChange={update("email")} className={inputCls} autoComplete="email" /></Field>
              <Field label={`${f.cruise} *`}>
                <select required value={form.cruise} onChange={update("cruise")} className={inputCls}>
                  <option value="" className="bg-emerald-deep text-white">{f.choose}</option>
                  {cruiseOptions.map((o) => <option key={o} value={o} className="bg-emerald-deep text-white">{o}</option>)}
                </select>
              </Field>
              <Field label={f.guests}><input type="number" min={1} max={18} value={form.guests} onChange={update("guests")} className={inputCls} /></Field>
              <Field label={f.date}><input type="date" value={form.date} onChange={update("date")} className={inputCls} /></Field>
              <div className="sm:col-span-2">
                <Field label={f.comments}><textarea rows={3} maxLength={1000} value={form.comments} onChange={update("comments")} className={inputCls} /></Field>
              </div>
            </div>
            <div className="mt-7 grid sm:grid-cols-[1.4fr_1fr] gap-3">
              <button
                type="button"
                onClick={(e) => sendWa(e as unknown as React.FormEvent)}
                onMouseEnter={() => setPrimary("wa")}
                onFocus={() => setPrimary("wa")}
                className="inline-flex items-center justify-center gap-2 px-9 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] transition-colors bg-[#25D366] text-[#001007] hover:bg-[#1DA851]"
              >
                <MessageCircle size={16} /> {f.sendWa}
              </button>
              <button
                type="button"
                disabled={status === "sending"}
                onClick={(e) => sendEmail(e as unknown as React.FormEvent)}
                onMouseEnter={() => setPrimary("email")}
                onFocus={() => setPrimary("email")}
                className="inline-flex items-center justify-center gap-2 px-6 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] transition-colors border border-[oklch(1_0_0/0.25)] text-[oklch(0.95_0.01_95)] hover:border-gold hover:text-gold disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <Mail size={16} /> {status === "sending" ? f.sending : f.sendEmail}
              </button>
            </div>
            {err && <p className="mt-3 text-xs text-[oklch(0.85_0.12_30)]">{err}</p>}
            {status === "ok" && (
              <p className="mt-3 text-xs rounded-md border border-[oklch(0.7_0.15_150/0.4)] bg-[oklch(0.7_0.15_150/0.12)] text-[oklch(0.92_0.08_150)] px-3 py-2">
                {f.sent}
              </p>
            )}
            {status === "error" && (
              <p className="mt-3 text-xs rounded-md border border-[oklch(0.7_0.15_60/0.4)] bg-[oklch(0.7_0.15_60/0.12)] text-[oklch(0.92_0.1_70)] px-3 py-2">
                {f.sendError}{" "}
                <a className="underline hover:text-gold" href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`${f.subtitle} — ${form.name} — ${form.cruise}`)}&body=${encodeURIComponent(message)}`}>
                  {CONTACT_EMAIL}
                </a>
                {errorDetail && (
                  <span className="block mt-2 font-mono text-[10px] opacity-80 break-all">
                    debug: {errorDetail}
                  </span>
                )}
              </p>
            )}
          </div>

          <aside className="space-y-5">
            <div className="grid gap-3 text-sm text-[oklch(0.9_0.02_95)] px-1">
              <a href={`tel:+${WHATSAPP}`} onClick={() => track("phone_click", { source: "contact_link" })} className="inline-flex items-center gap-2 hover:text-gold"><Phone size={14} className="text-gold" /> +351 939 418 375</a>
              <a href={WHATSAPP_LINK(contactWaMsg)} target="_blank" rel="noopener noreferrer" onClick={() => track("whatsapp_click", { source: "contact_link" })} className="inline-flex items-center gap-2 hover:text-gold"><MessageCircle size={14} className="text-gold" /> +351 923 338 708 <span className="text-[oklch(0.78_0.02_95)]">({lang === "pt" ? "apenas chat WhatsApp" : "WhatsApp chat only"})</span></a>
              <a href={`mailto:${CONTACT_EMAIL}`} onClick={() => track("email_form_submit", { source: "contact_link" })} className="inline-flex items-center gap-2 hover:text-gold"><Mail size={14} className="text-gold" /> {CONTACT_EMAIL}</a>
              <p className="inline-flex items-center gap-2"><MapPin size={14} className="text-gold" /> Marina de Vilamoura, Gate A2</p>
            </div>
          </aside>
        </form>
      </div>
    </section>
  );
}

const inputCls = "w-full bg-[oklch(1_0_0/0.06)] border border-[oklch(1_0_0/0.12)] rounded-md px-3 py-2.5 text-sm text-[oklch(0.98_0.01_95)] placeholder:text-[oklch(0.7_0.02_95)] focus:outline-none focus:border-gold transition-colors";
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.22em] text-[oklch(0.78_0.02_95)] mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function SeoCopy({ lang }: { lang: Lang }) {
  const c = t[lang].seoCopy;
  return (
    <section className="bg-sand py-20">
      <div className="mx-auto max-w-4xl px-5">
        <p className="eyebrow mb-4">{c.eyebrow}</p>
        <h2 className="font-display text-3xl sm:text-4xl text-emerald-deep leading-tight">{c.heading}</h2>
        <div className="mt-8 space-y-5 text-[15px] leading-relaxed text-foreground/85">
          {c.body.map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </div>
    </section>
  );
}

export function Footer({ lang }: { lang: Lang }) {
  const dict = t[lang];
  const f = dict.footer;
  const waMsg = useWhatsappGreeting(lang);
  const nav = [
    { href: "#experiences", l: dict.nav.experiences },
    { href: "#inclusions", l: dict.nav.inclusions },
    { href: "#upgrades", l: dict.nav.upgrades },
    { href: "#yacht", l: dict.nav.yacht },
    { href: "#about", l: dict.nav.about },
    { href: "#crew", l: dict.nav.crew },
  ];
  const faqFullHref = lang === "pt" ? "/pt/faq" : "/faq";
  const faqFullLabel = lang === "pt" ? "Perguntas Frequentes" : "Frequent Questions";
  return (
    <footer className="bg-emerald-deep text-[oklch(0.88_0.02_95)] pt-16 pb-8 border-t border-[oklch(0.78_0.14_85/0.2)]">
      <div className="mx-auto max-w-7xl px-5 grid gap-10 md:grid-cols-4">
        <div className="md:col-span-1">
          <p className="font-display text-2xl tracking-[0.18em] uppercase text-[oklch(0.98_0.01_95)]">Morning<span className="text-gold">star</span></p>
          <p className="mt-4 text-sm leading-relaxed">{f.tagline}</p>
          <p className="mt-4 text-xs uppercase tracking-widest text-gold">{f.licence}</p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">{f.navTitle}</p>
          <ul className="mt-4 space-y-2 text-sm">
            {nav.map((n) => <li key={n.href}><a href={n.href} className="hover:text-gold transition-colors">{n.l}</a></li>)}
            <li><Link to={faqFullHref} className="hover:text-gold transition-colors">{faqFullLabel}</Link></li>
          </ul>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">{f.contactTitle}</p>
          <ul className="mt-4 space-y-3 text-sm">
            <li className="flex items-start gap-2"><MapPin size={14} className="text-gold mt-1 shrink-0" /><span>{f.address}<br />{f.city}</span></li>
            <li><a href={`tel:+${WHATSAPP}`} onClick={() => track("phone_click", { source: "footer_link" })} className="inline-flex items-center gap-2 hover:text-gold"><Phone size={14} className="text-gold" /> +351 939 418 375</a></li>
            <li><a href={`mailto:${CONTACT_EMAIL}`} onClick={() => track("email_form_submit", { source: "footer_link" })} className="inline-flex items-center gap-2 hover:text-gold"><Mail size={14} className="text-gold" /> {CONTACT_EMAIL}</a></li>
            <li><a href={WHATSAPP_LINK(waMsg)} onClick={() => track("whatsapp_click", { source: "footer" })} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 hover:text-gold"><MessageCircle size={14} className="text-gold" /> +351 923 338 708 <span className="opacity-80">({lang === "pt" ? "apenas chat WhatsApp" : "WhatsApp chat only"})</span></a></li>
          </ul>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">{f.legalTitle}</p>
          <ul className="mt-4 space-y-2 text-sm">
            <li>RNAAT 743/2022</li>
            <li>Turismo de Portugal · Turismo 360</li>
            <li>
              <a
                href="https://www.livroreclamacoes.pt/Inicio/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-gold transition-colors"
              >
                <BookOpen size={14} className="text-gold" />
                {lang === "en" ? "Complaints Book" : "Livro de Reclamações"}
                <ExternalLink size={11} className="opacity-60" />
              </a>
            </li>
            <li>
              <a
                href="https://www.consumoalgarve.pt/index.php/pt/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-gold transition-colors"
              >
                {lang === "en" ? "Consumer Arbitration (CIMAAL)" : "Centro de Arbitragem (CIMAAL)"}
                <ExternalLink size={11} className="opacity-60" />
              </a>
            </li>
            <li>
              <Link
                to={lang === "en" ? "/terms" : "/pt/termos"}
                className="hover:text-gold transition-colors"
              >
                {f.terms}
              </Link>
            </li>
            <li>
              <Link
                to={lang === "en" ? "/cookie-policy" : "/pt/politica-de-cookies"}
                className="hover:text-gold transition-colors"
              >
                {lang === "en" ? "Cookie Policy" : "Política de Cookies"}
              </Link>
            </li>
            <li>
              <Link
                to={lang === "en" ? "/trust-centre" : "/pt/centro-de-confianca"}
                className="hover:text-gold transition-colors"
              >
                {lang === "en" ? "Trust Centre" : "Centro de Confiança"}
              </Link>
            </li>
            <li><Link to="/" className="hover:text-gold transition-colors">EN</Link> · <Link to="/pt" className="hover:text-gold transition-colors">PT</Link></li>
          </ul>
          <p className="mt-6 text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">{lang === "en" ? "Follow" : "Siga-nos"}</p>
          <div className="mt-4 flex items-center gap-3">
            <a
              href="https://www.facebook.com/vilamouradreamscruises"
              target="_top"
              rel="noopener noreferrer"
              aria-label="Facebook"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[oklch(1_0_0/0.15)] hover:border-gold hover:text-gold transition-colors"
            >
              <Facebook size={15} />
            </a>
            <a
              href="https://www.instagram.com/vilamouradreamscruises/"
              target="_top"
              rel="noopener noreferrer"
              aria-label="Instagram"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[oklch(1_0_0/0.15)] hover:border-gold hover:text-gold transition-colors"
            >
              <Instagram size={15} />
            </a>
            <a
              href="https://www.tripadvisor.com/Attraction_Review-g227947-d26258230-Reviews-Vilamoura_Dreams_Cruises-Vilamoura_Quarteira_Faro_District_Algarve.html"
              target="_top"
              rel="noopener noreferrer"
              aria-label="Tripadvisor"
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[oklch(1_0_0/0.15)] hover:border-gold hover:text-gold transition-colors"
            >
              <svg viewBox="0 0 512 512" width="15" height="15" fill="currentColor" aria-hidden="true">
                <path d="M256.2 183.8c-44.5 0-80.7 36.1-80.7 80.7s36.2 80.7 80.7 80.7 80.7-36.1 80.7-80.7-36.1-80.7-80.7-80.7zm0 129.4c-26.9 0-48.7-21.8-48.7-48.7s21.8-48.7 48.7-48.7 48.7 21.8 48.7 48.7-21.8 48.7-48.7 48.7zM128.1 240.7c-13.1 0-23.7 10.6-23.7 23.8 0 13.1 10.6 23.8 23.7 23.8s23.8-10.6 23.8-23.8c-.1-13.1-10.7-23.8-23.8-23.8zm256.1 0c-13.1 0-23.7 10.6-23.7 23.8 0 13.1 10.6 23.8 23.7 23.8s23.8-10.6 23.8-23.8c0-13.1-10.7-23.8-23.8-23.8zM427 209.2c14.6-20.1 24.2-44.6 27.7-71.4l-44.7-32.4c.2-.1.4-.3.7-.4-31.7-15.5-69.3-24.5-110.4-24.5-44 0-83.9 10.3-116.8 27.8H97.3l37.7 41.1C107.7 175.6 89.5 215.9 89.5 261c0 92 74.6 166.6 166.6 166.6 41.1 0 78.8-14.9 107.8-39.6l40 43.5 26.4-72.8c20.8-23 33.4-53.3 33.4-86.6.1-23-6-44.6-16.7-62.9zM256.2 391.6c-70.1 0-127-56.9-127-127.1S186 137.5 256.2 137.5s127 56.9 127 127.1c-.1 70.1-57 127-127 127z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-7xl px-5 mt-12 pt-6 border-t border-[oklch(1_0_0/0.08)] flex flex-col sm:flex-row gap-3 items-center justify-between text-xs text-[oklch(0.78_0.02_95)]">
        <p>© {new Date().getFullYear()} Morningstar Yacht · {f.rights}</p>
        <p>Marina de Vilamoura · Algarve · Portugal</p>
      </div>
      {lang === "en" && (
        <div className="mx-auto max-w-7xl px-5 mt-6 text-[11px] uppercase tracking-[0.18em] text-[oklch(0.7_0.02_95)] flex flex-wrap gap-x-4 gap-y-2 justify-center">
          <Link to="/benagil-cave-tour" className="hover:text-gold transition-colors">Benagil Cave Tour</Link>
          <span className="opacity-30">·</span>
          <Link to="/vilamoura-boat-trips" className="hover:text-gold transition-colors">Vilamoura Boat Trips</Link>
          <span className="opacity-30">·</span>
          <Link to="/sunset-cruise-vilamoura" className="hover:text-gold transition-colors">Sunset Cruise Vilamoura</Link>
          <span className="opacity-30">·</span>
          <Link to="/private-yacht-charter-vilamoura" className="hover:text-gold transition-colors">Private Yacht Charter</Link>
        </div>
      )}
    </footer>
  );
}

export default function SitePage({
  lang,
  heroOverride,
  pageContent,
}: {
  lang: Lang;
  heroOverride?: HeroOverride;
  pageContent?: PageContent;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const meta = ROUTE_META[pathname];
  return (
    <>
      <Header lang={lang} />
      <main>
        <Hero lang={lang} override={heroOverride} />
        {meta && <Breadcrumbs lang={lang} pathname={pathname} />}
        <TrustBar lang={lang} />
        {pageContent && <PageIntro lang={lang} content={pageContent} />}
        <Experiences lang={lang} />
        <Inclusions lang={lang} />
        <Upgrades lang={lang} />
        <Yacht lang={lang} />
        <AboutUs lang={lang} />
        <LicencesStrip lang={lang} />
        <ExcellenceCrew lang={lang} />
        <Gallery lang={lang} />
        <Testimonials lang={lang} />
        <FAQ lang={lang} />
        <Contact lang={lang} />
      <SeoCopy lang={lang} />
      {meta && <RelatedCruises lang={lang} pathname={pathname} />}
      </main>
      <Footer lang={lang} />
      <FloatingWhatsApp lang={lang} />
      <Suspense fallback={null}>
        <CookieConsent lang={lang} />
      </Suspense>
    </>
  );
}

function Breadcrumbs({ lang, pathname }: { lang: Lang; pathname: string }) {
  const meta = ROUTE_META[pathname];
  if (!meta) return null;
  const homeLabel = lang === "pt" ? "Início" : "Home";
  const homeHref = lang === "pt" ? "/pt" : "/";
  return (
    <nav aria-label="Breadcrumb" className="bg-sand border-b border-[oklch(0.78_0.14_85/0.2)]">
      <ol className="mx-auto max-w-7xl px-5 py-3 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-emerald-deep/70">
        <li>
          <Link to={homeHref} className="hover:text-gold-deep transition-colors">
            {homeLabel}
          </Link>
        </li>
        <li aria-hidden="true" className="text-gold-deep">›</li>
        <li className="text-emerald-deep/50">{meta.parentLabel}</li>
        <li aria-hidden="true" className="text-gold-deep">›</li>
        <li className="text-emerald-deep font-semibold tracking-[0.16em]" aria-current="page">
          {meta.name}
        </li>
      </ol>
    </nav>
  );
}

function PageIntro({ lang, content }: { lang: Lang; content: PageContent }) {
  const ctaLabel = lang === "pt" ? "Ver experiências" : "View experiences";
  const waLabel = "Concierge";
  const faqLabel = lang === "pt" ? "Perguntas frequentes" : "Frequently asked";
  const waMsg = useWhatsappGreeting(lang);
  return (
    <section className="bg-white border-b border-emerald-deep/10">
      <div className="mx-auto max-w-5xl px-5 py-20 sm:py-24">
        <p className="eyebrow mb-4 text-gold-deep">{content.eyebrow}</p>
        <h1 className="font-display text-3xl sm:text-5xl text-emerald-deep leading-[1.08] max-w-3xl">
          {content.h1}
        </h1>
        <div className="mt-8 space-y-5 max-w-3xl text-base sm:text-lg text-foreground/80 leading-relaxed">
          {content.intro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
        <ul className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-3 max-w-3xl">
          {content.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 text-sm sm:text-base text-emerald-deep">
              <span aria-hidden="true" className="text-gold-deep mt-1">›</span>
              <span>{b}</span>
            </li>
          ))}
        </ul>
        <div className="mt-10 flex flex-wrap gap-3">
          <a
            href="#experiences"
            className="inline-flex items-center bg-gold px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-deep hover:bg-[oklch(0.85_0.14_85)] transition-colors"
          >
            {ctaLabel}
          </a>
          <a
            href={WHATSAPP_LINK(waMsg)}
            onClick={() => track("whatsapp_click", { source: content.whatsappSource })}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#25D366] px-7 py-3.5 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#001007] hover:bg-[#1ebe57] transition-colors"
          >
            <MessageCircle size={14} />
            {waLabel}
          </a>
        </div>
        <div className="mt-16 max-w-3xl">
          <h2 className="font-display text-2xl sm:text-3xl text-emerald-deep mb-6">
            {faqLabel}
          </h2>
          <dl className="divide-y divide-emerald-deep/10 border-y border-emerald-deep/10">
            {content.faqs.map((f, i) => (
              <div key={i} className="py-5">
                <dt className="font-semibold text-emerald-deep">{f.q}</dt>
                <dd className="mt-2 text-foreground/75 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-6">
            <Link to={lang === "pt" ? "/pt/faq" : "/faq"} className="inline-block text-[11px] uppercase tracking-[0.22em] text-gold-deep font-semibold hover:text-emerald-deep transition-colors">
              {lang === "pt" ? "Ver as 15 perguntas completas →" : "See the full 15-question FAQ →"}
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function RelatedCruises({ lang, pathname }: { lang: Lang; pathname: string }) {
  const related = getRelatedRoutes(pathname, 4);
  if (related.length === 0) return null;
  const heading = lang === "pt" ? "Outros cruzeiros que pode gostar" : "More cruises you may like";
  const cta = lang === "pt" ? "Ver" : "Explore";
  return (
    <section className="bg-background py-20 border-t border-[oklch(0.78_0.14_85/0.2)]">
      <div className="mx-auto max-w-7xl px-5">
        <h2 className="font-display text-2xl sm:text-3xl text-emerald-deep mb-10">{heading}</h2>
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {related.map((r) => (
            <li key={r.path}>
              <Link
                to={r.path}
                className="group block border border-[oklch(0.78_0.14_85/0.3)] bg-card p-5 hover:border-gold hover:shadow-gold transition-all"
              >
                <span className="block font-display text-lg text-emerald-deep leading-snug group-hover:text-gold-deep transition-colors">
                  {r.name}
                </span>
                <span className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-gold-deep font-semibold">
                  {cta} <ChevronRight size={11} />
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

export function FloatingWhatsApp({ lang }: { lang: Lang }) {
  const msg = useWhatsappGreeting(lang);
  return (
    <a
      href={WHATSAPP_LINK(msg)}
      onClick={() => track("whatsapp_click", { source: "floating_button" })}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp"
      className="fixed right-5 z-[60] inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#25D366] text-[#001007] shadow-2xl ring-2 ring-white/20 hover:scale-105 active:scale-95 transition-transform"
      style={{ bottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px))" }}
    >
      <svg viewBox="0 0 32 32" width="28" height="28" fill="currentColor" aria-hidden="true">
        <path d="M19.11 17.21c-.27-.14-1.6-.79-1.85-.88-.25-.09-.43-.14-.61.14-.18.27-.7.88-.86 1.06-.16.18-.32.2-.59.07-.27-.14-1.14-.42-2.17-1.34-.8-.71-1.34-1.6-1.5-1.87-.16-.27-.02-.42.12-.55.12-.12.27-.32.41-.48.14-.16.18-.27.27-.45.09-.18.05-.34-.02-.48-.07-.14-.61-1.47-.84-2.01-.22-.53-.45-.46-.61-.47l-.52-.01c-.18 0-.48.07-.73.34-.25.27-.95.93-.95 2.27 0 1.34.98 2.64 1.11 2.82.14.18 1.93 2.95 4.68 4.14.65.28 1.16.45 1.56.58.65.21 1.25.18 1.72.11.52-.08 1.6-.65 1.83-1.28.23-.63.23-1.18.16-1.28-.06-.11-.24-.18-.5-.32zM16.02 5.33c-5.91 0-10.7 4.79-10.7 10.7 0 1.88.49 3.72 1.43 5.34L5.2 26.67l5.46-1.43a10.66 10.66 0 005.36 1.46h.01c5.91 0 10.7-4.79 10.7-10.7 0-2.86-1.11-5.55-3.13-7.57a10.62 10.62 0 00-7.58-3.1zm0 19.6h-.01a8.88 8.88 0 01-4.53-1.24l-.32-.19-3.24.85.86-3.16-.21-.33a8.87 8.87 0 01-1.36-4.74c0-4.91 4-8.91 8.92-8.91 2.38 0 4.62.93 6.3 2.61a8.85 8.85 0 012.61 6.31c0 4.91-4 8.9-8.92 8.9z"/>
      </svg>
    </a>
  );
}

function WhatsAppPreview({ lang }: { lang: Lang }) {
  const greeting = lang === "en"
    ? "Hi! 👋 Welcome to Morningstar Vilamoura. How can we help you with your charter?"
    : "Olá! 👋 Bem-vindo à Morningstar Vilamoura. Como podemos ajudar com o seu charter?";
  const placeholder = lang === "en" ? "Type a message" : "Escreva uma mensagem";
  const fallback = lang === "en"
    ? "Hello Morningstar, I'd like to enquire about a charter."
    : "Olá Morningstar, gostaria de informações sobre um charter.";
  const [text, setText] = useState("");
  const [now, setNow] = useState("");

  useEffect(() => {
    setNow(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
  }, []);

  const send = () => {
    const message = text.trim() || fallback;
    openWhatsApp(message);
  };

  const chatBg = {
    backgroundColor: "#ECE5DD",
    backgroundImage:
      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 80 80'><g fill='%23d9d2c8' fill-opacity='0.35'><circle cx='10' cy='10' r='1.2'/><circle cx='30' cy='25' r='1'/><circle cx='55' cy='15' r='1.4'/><circle cx='70' cy='45' r='1'/><circle cx='20' cy='60' r='1.3'/><circle cx='45' cy='70' r='1'/></g></svg>\")",
  } as const;

  return (
    <div className="overflow-hidden rounded-2xl shadow-soft border border-black/10 bg-white">
      <div className="flex items-center gap-3 px-4 py-3" style={{ backgroundColor: "#075E54", color: "white" }}>
        <div className="h-10 w-10 rounded-full bg-gold text-emerald-deep flex items-center justify-center font-display text-sm font-semibold shadow">MS</div>
        <div className="flex-1 leading-tight">
          <p className="text-[15px] font-medium">Morningstar — Vilamoura</p>
          <p className="text-[11px] text-white/80">online</p>
        </div>
      </div>
      <div style={chatBg} className="px-4 py-5 min-h-[180px]">
        <div className="max-w-[85%] relative rounded-lg rounded-tl-none bg-white px-3 py-2 shadow-sm">
          <span aria-hidden className="absolute -left-1.5 top-0 h-3 w-3 bg-white" style={{ clipPath: "polygon(100% 0, 0 0, 100% 100%)" }} />
          <p className="text-[13px] text-[#111b21] leading-snug whitespace-pre-line">{greeting}</p>
          <p suppressHydrationWarning className="mt-1 text-right text-[10px] text-[#667781]">{now}</p>
        </div>
      </div>
      <div className="flex items-end gap-2 px-2 py-2 bg-[#F0F0F0]">
        <div className="flex-1 rounded-3xl bg-white px-3 py-2 shadow-sm">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder={placeholder}
            className="w-full resize-none bg-transparent text-[14px] text-[#111b21] placeholder:text-[#667781] outline-none max-h-24"
          />
        </div>
        <button onClick={send} aria-label="Send" className="h-10 w-10 shrink-0 rounded-full flex items-center justify-center text-white shadow" style={{ backgroundColor: "#25D366" }}>
          <Send size={18} />
        </button>
      </div>
    </div>
  );
}